import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { fetchExternalApplications } from "../../Apis/Wallet-api";
import { formatLabel, formatMoney } from "../../Utils/walletFormat";
import { isWalletAgentUser, isWalletClientUser } from "../../Utils/walletAdminAccess";
import useAuthStore from "../../Zustand/Store/useAuthStore";
import Layout4 from "../DataLayouts/Layout4";

function statusChipColor(status) {
  if (status === "active") return "success";
  if (status === "suspended") return "warning";
  return "default";
}

export default function WalletApplicationsList() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const isClientUser = isWalletClientUser(userData);
  const isAgentUser = isWalletAgentUser(userData);
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const appRows = await fetchExternalApplications();
      setApps(Array.isArray(appRows) ? appRows : []);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to load external applications."
      );
      setApps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
      <Layout4
        heading="Agents"
        subtitle={
          isAgentUser
            ? "Your linked agent — balance, credit, ledger, and access controls"
            : isClientUser
              ? "Your wallet agents — balance, credit, ledger, and access controls"
              : "Wallet agents — balance, credit, ledger, and access controls"
        }
        showAddButton={false}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : apps.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">
            {isAgentUser
              ? "No agent is linked to your account yet. Contact an administrator."
              : "No agents yet."}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                {!isClientUser && !isAgentUser && <TableCell>Client</TableCell>}
                <TableCell>Application user ID</TableCell>
                <TableCell>API key</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell align="right">Credit used</TableCell>
                <TableCell align="right">Credit limit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apps.map((app) => (
                <TableRow
                  key={app.id}
                  hover
                  sx={{ cursor: app.wallet_id ? "pointer" : "default" }}
                  onClick={() => {
                    if (app.wallet_id) {
                      navigate(`/wallet/applications/${app.wallet_id}`, {
                        state: { appName: app.name, externalAppId: app.id },
                      });
                    }
                  }}
                >
                  <TableCell>{app.id}</TableCell>
                  <TableCell>{app.name}</TableCell>
                  {!isClientUser && !isAgentUser && (
                    <TableCell>
                      {app.client_name ||
                        (app.client_id ? `#${app.client_id}` : "-")}
                    </TableCell>
                  )}
                  <TableCell>
                    {app.application_user_id != null
                      ? app.application_user_id
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {app.api_key}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={formatLabel(app.status || "unknown")}
                      color={statusChipColor(app.status)}
                    />
                  </TableCell>
                  <TableCell align="right">{formatMoney(app.balance)}</TableCell>
                  <TableCell align="right">{formatMoney(app.credit_used)}</TableCell>
                  <TableCell align="right">{formatMoney(app.credit_limit)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
